/**
 * Izračun pripreme startera — unatrag, od ciljane mase prema osvježavanju.
 *
 * Svaki stupanj startera je jedno osvježavanje: uzme se dio zrelog startera,
 * doda mu se brašno u zadanom omjeru i voda u postotku na to brašno. Masa koja
 * iz toga izađe je
 *
 *   ciljana_masa = starter × (1 + omjer_brašna + omjer_brašna × hidratacija)
 *
 * pa se iz poznate ciljane mase dijeljenjem tim faktorom dobije koliko startera
 * treba krenuti — i onda brašno i voda iz njega. Zato je zbroj sastojaka uvijek
 * točno jednak ciljanoj masi, što stari izračun (množenje ciljane mase fiksnim
 * koeficijentima) nije davao: proizvodio je ~72% tražene količine.
 *
 * Lanac je jedan i ide unatrag kroz dan:
 *
 *   matica (jučer) → NOĆNI → JUTARNJI  → radni nalog
 *                          ↘ RAŽENI    → radni nalog
 *   jutarnji → DNEVNI/MATICA → sutra
 *
 * Noćni starter hrani i jutarnji i raženi, pa se njegova ciljana masa dobije
 * zbrajanjem onoga što ta dva stupnja traže — ne računa se zasebno po stupnju.
 */

import { STARTER_SETTING_KEYS } from '../../../types/mes';

/** Tehnološki parametri pripreme; dolaze iz MesSettinga, mijenja ih tehnolog. */
export interface StarterParams {
  /** Sigurnosna količina povrh radnog naloga, u kg. */
  sigurnosnaKg: number;
  /** Količina dnevnog startera (matice) koja ostaje za idući dan, u kg. */
  dnevnaMaticaKg: number;
  omjerBrasnoNocni: number;
  hidratacijaNocni: number;
  omjerBrasnoJutro: number;
  hidratacijaJutro: number;
  omjerBrasnoRazeni: number;
  hidratacijaRazeni: number;
  omjerBrasnoDnevni: number;
  hidratacijaDnevni: number;
  /** Kapacitet miksera po zamjesu, u kg — iz Machine.CapacityMaxKg. */
  kapacitetMikseraKg: number;
}

/** Vrijednosti s kojima se recept vodi u pogonu; fallback ako postavka fali. */
export const DEFAULT_STARTER_PARAMS: StarterParams = {
  sigurnosnaKg: 100,
  dnevnaMaticaKg: 75,
  omjerBrasnoNocni: 8,
  hidratacijaNocni: 0.6,
  omjerBrasnoJutro: 0.3,
  hidratacijaJutro: 0.6,
  omjerBrasnoRazeni: 1,
  hidratacijaRazeni: 0.6,
  omjerBrasnoDnevni: 1,
  hidratacijaDnevni: 0.5,
  kapacitetMikseraKg: 200,
};

/** Jedno osvježavanje: koliko startera, brašna i vode ide u zamjes. */
export interface StarterStupanj {
  /** Zreli starter koji ulazi u osvježavanje. */
  starter: number;
  brasno: number;
  voda: number;
  /** Zbroj sastojaka = ciljana masa stupnja. */
  ukupno: number;
}

export interface NocniStupanj extends StarterStupanj {
  nZamjesa: number;
  poZamjesu: StarterStupanj;
}

export interface StarterPlanResult {
  /** Radni nalog + sigurnosna količina. */
  ciljJutro: number;
  ciljRazeni: number;
  ciljDnevni: number;
  nocni: NocniStupanj;
  jutro: StarterStupanj;
  razeni: StarterStupanj;
  dnevni: StarterStupanj;
}

const prazanStupanj: StarterStupanj = { starter: 0, brasno: 0, voda: 0, ukupno: 0 };

/**
 * Razloži ciljanu masu na starter + brašno + vodu za zadani omjer i hidrataciju.
 *
 * Nula za omjer brašna je legitimna postavka (stupanj bez osvježavanja, sve je
 * starter), pa faktor nikad ne može biti nula i dijeljenje je sigurno.
 */
export function razloziStupanj(
  ciljnaMasa: number,
  omjerBrasno: number,
  hidratacija: number
): StarterStupanj {
  if (!Number.isFinite(ciljnaMasa) || ciljnaMasa <= 0) return { ...prazanStupanj };
  const omjer = Math.max(0, omjerBrasno || 0);
  const hidr = Math.max(0, hidratacija || 0);
  const faktor = 1 + omjer + omjer * hidr;
  const starter = ciljnaMasa / faktor;
  const brasno = starter * omjer;
  const voda = brasno * hidr;
  return { starter, brasno, voda, ukupno: starter + brasno + voda };
}

/**
 * Cijeli plan pripreme za jedan proizvodni dan.
 *
 * @param starterRadniNalogKg zbroj startera iz potvrđenog plana
 * @param razeniRadniNalogKg  zbroj raženog startera iz potvrđenog plana
 */
export function izracunajPlanStartera(
  starterRadniNalogKg: number,
  razeniRadniNalogKg: number,
  params: StarterParams
): StarterPlanResult {
  const radniNalog = Math.max(0, starterRadniNalogKg || 0);
  // Sigurnosna količina je rezerva na traženu, ne razlog da se starter uopće
  // radi: dan bez startera u planu (npr. samo biga) ne smije dobiti zamjes.
  const ciljJutro = radniNalog > 0 ? radniNalog + Math.max(0, params.sigurnosnaKg || 0) : 0;
  const ciljRazeni = Math.max(0, razeniRadniNalogKg || 0);
  // Matica se osvježava iz jutarnjeg startera, pa je bez njega nema od čega.
  const ciljDnevni = ciljJutro > 0 ? Math.max(0, params.dnevnaMaticaKg || 0) : 0;

  const jutro = razloziStupanj(ciljJutro, params.omjerBrasnoJutro, params.hidratacijaJutro);
  const razeni = razloziStupanj(ciljRazeni, params.omjerBrasnoRazeni, params.hidratacijaRazeni);

  // Oba stupnja troše isti noćni starter, pa je njegova ciljana masa zbroj.
  const ciljNocni = jutro.starter + razeni.starter;
  const nocniBase = razloziStupanj(ciljNocni, params.omjerBrasnoNocni, params.hidratacijaNocni);

  const kapacitet = params.kapacitetMikseraKg > 0 ? params.kapacitetMikseraKg : 0;
  const nZamjesa =
    nocniBase.ukupno > 0 && kapacitet > 0 ? Math.ceil(nocniBase.ukupno / kapacitet) : 0;
  const podijeli = (n: number) => (nZamjesa > 0 ? n / nZamjesa : 0);

  const dnevni = razloziStupanj(ciljDnevni, params.omjerBrasnoDnevni, params.hidratacijaDnevni);

  return {
    ciljJutro,
    ciljRazeni,
    ciljDnevni,
    nocni: {
      ...nocniBase,
      nZamjesa,
      poZamjesu: {
        starter: podijeli(nocniBase.starter),
        brasno: podijeli(nocniBase.brasno),
        voda: podijeli(nocniBase.voda),
        ukupno: podijeli(nocniBase.ukupno),
      },
    },
    jutro,
    razeni,
    dnevni,
  };
}

/**
 * Postavke koje se čuvaju u MesSettingu. `kapacitetMikseraKg` nije među njima —
 * on se čita iz Machine.CapacityMaxKg odabranog miksera, da kapacitet ostane
 * jedan podatak u registru strojeva umjesto da se prepisuje na dva mjesta.
 */
const PARAM_SETTING_KEY: Record<
  Exclude<keyof StarterParams, 'kapacitetMikseraKg'>,
  string
> = {
  sigurnosnaKg: STARTER_SETTING_KEYS.SAFETY_KG,
  dnevnaMaticaKg: STARTER_SETTING_KEYS.DAILY_MOTHER_KG,
  omjerBrasnoNocni: STARTER_SETTING_KEYS.NIGHT_FLOUR_RATIO,
  hidratacijaNocni: STARTER_SETTING_KEYS.NIGHT_HYDRATION,
  omjerBrasnoJutro: STARTER_SETTING_KEYS.MORNING_FLOUR_RATIO,
  hidratacijaJutro: STARTER_SETTING_KEYS.MORNING_HYDRATION,
  omjerBrasnoRazeni: STARTER_SETTING_KEYS.RYE_FLOUR_RATIO,
  hidratacijaRazeni: STARTER_SETTING_KEYS.RYE_HYDRATION,
  omjerBrasnoDnevni: STARTER_SETTING_KEYS.DAILY_FLOUR_RATIO,
  hidratacijaDnevni: STARTER_SETTING_KEYS.DAILY_HYDRATION,
};

type SettingRow = { SettingKey: string; SettingValue?: string | null };

/** MesSetting redci -> parametri; neispravna vrijednost pada na zadanu. */
export function starterParamsFromSettings(
  rows: SettingRow[] | null | undefined,
  kapacitetMikseraKg?: number | null
): StarterParams {
  const byKey = new Map((rows ?? []).map((r) => [r.SettingKey, r.SettingValue]));
  const params = { ...DEFAULT_STARTER_PARAMS };
  (Object.keys(PARAM_SETTING_KEY) as Array<keyof typeof PARAM_SETTING_KEY>).forEach((field) => {
    const raw = byKey.get(PARAM_SETTING_KEY[field]);
    if (raw == null || raw === '') return;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) params[field] = n;
  });
  if (Number.isFinite(kapacitetMikseraKg) && (kapacitetMikseraKg as number) > 0) {
    params.kapacitetMikseraKg = kapacitetMikseraKg as number;
  }
  return params;
}

/** Parametri -> payload za PUT /settings (bez kapaciteta, koji nije postavka). */
export function starterParamsToSettings(params: StarterParams): Record<string, string> {
  const values: Record<string, string> = {};
  (Object.keys(PARAM_SETTING_KEY) as Array<keyof typeof PARAM_SETTING_KEY>).forEach((field) => {
    values[PARAM_SETTING_KEY[field]] = String(params[field]);
  });
  return values;
}

/** Odabrani mikser iz postavki, ili null dok tehnolog ne izabere. */
export function starterMixerIdFromSettings(rows: SettingRow[] | null | undefined): number | null {
  const raw = (rows ?? []).find((r) => r.SettingKey === STARTER_SETTING_KEYS.MIXER_MACHINE_ID)
    ?.SettingValue;
  if (raw == null || raw === '') return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Biga se ne osvježava — omjeri su fiksni udjeli koji daju 1 kg bige. */
export const BIGA_UDJELI = { brasno: 0.6132, kvasac: 0.0005, voda: 0.3863 } as const;

export interface BigaCalc {
  brasno: number;
  kvasac: number;
  voda: number;
  ukupno: number;
}

export function izracunajBigu(ukupnoKg: number): BigaCalc {
  const masa = Number.isFinite(ukupnoKg) && ukupnoKg > 0 ? ukupnoKg : 0;
  return {
    brasno: masa * BIGA_UDJELI.brasno,
    kvasac: masa * BIGA_UDJELI.kvasac,
    voda: masa * BIGA_UDJELI.voda,
    ukupno: masa,
  };
}
